import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  createExerciseDetailsSchema,
  ExerciseDetails,
  ExerciseForm,
  ExerciseFormSchema,
  ExerciseMetricSelection,
  ExerciseMetricSelectionSchema,
  zIntegrationPropertiesFormSchema,
} from '@strength/domain';
import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../../trpc/react';
import Badge, {
  defaultExerciseBadge,
} from '../../../../components/badge/badge';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import { Form } from '../../../../components/form/form';
import {
  DeleteConfirmationModal,
  deleteExerciseText,
} from '../../../../components/modals/delete-confirmation-modal';
import { useViewer } from '../../../../context/viewer';
import { LibraryExercise } from '../types';
import { ExerciseFormContent } from './ExerciseFormContent';

type CreateEditExerciseSheetProps = {
  open: boolean;
  onOpenChange: () => void;
  defaultValues?: Partial<ExerciseForm>;
  teamId?: string;
  showTeamName?: boolean;
} & (
  | {
      exercise: LibraryExercise;
      onSubmit: (params: { id: string; exercise: ExerciseForm }) => void;
      onDelete: (exercise: LibraryExercise) => void;
    }
  | {
      exercise?: undefined;
      onSubmit: (params: { exercise: ExerciseForm }) => void;
      onDelete?: never;
    }
);

export const CreateEditExerciseSheet = ({
  exercise,
  open,
  onOpenChange,
  onSubmit,
  onDelete,
  defaultValues,
  teamId,
  showTeamName = false,
}: CreateEditExerciseSheetProps) => {
  const {
    palette: {
      mediumEmphasisTextLight,
      mediumEmphasisTextDark,
      common: { white },
    },
  } = useTheme();
  const { t } = useTranslation();

  const isEdit = !!exercise;
  const isDefault = exercise?.isDefault;
  const [step, setStep] = useState<'details' | 'metrics'>('details');
  const [detailsData, setDetailsData] = useState<ExerciseDetails>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const {
    org: {
      settings: { enableTeamLibrary, requireExerciseMetadataFields },
    },
    team: { selectedTeam, teams },
    hasPermissions,
  } = useViewer();
  const disableEdit = isEdit && !hasPermissions('library.exercises', 'edit');
  const disableCreate = !isEdit && !hasPermissions('library.exercises', 'create');
  const disableWrite = isEdit ? disableEdit : disableCreate;
  const disableDelete = !hasPermissions('library.exercises', 'delete');

  const bodyPartsQuery = trpc.bodyPart.fetch.useQuery({});
  const bodyParts = bodyPartsQuery.data || [];
  const equipmentQuery = trpc.equipment.fetch.useQuery({});
  const equipment = equipmentQuery.data || [];
  const trainingTypesQuery = trpc.trainingType.fetch.useQuery({});
  const trainingTypes = trainingTypesQuery.data || [];
  const tagsQuery = trpc.tag.tags.useQuery({
    types: ['exercise'],
  });
  const tags = tagsQuery.data || [];

  const exerciseTeamId =
    (isEdit ? exercise?.teamId : selectedTeam) || undefined;

  const detailsSchema = createExerciseDetailsSchema({
    requireMetadataFields: requireExerciseMetadataFields,
  }).refine((data) => data.trainingType !== null, {
    message: 'trainingTypeRequired',
    path: ['trainingType'],
  });

  const stepSchema =
    step === 'details' ? detailsSchema : ExerciseMetricSelectionSchema;

  const form = useForm<ExerciseForm>({
    resolver: zodResolver(stepSchema),
    defaultValues: exercise
      ? getValuesForExerciseForm(exercise)
      : createDefaultExerciseForm({
          ...defaultValues,
          teamId: enableTeamLibrary ? exerciseTeamId : undefined,
        }),
  });
  const { handleSubmit, reset } = form;
  const name = form.watch('name');
  const [debouncedName, setDebouncedName] = useState(name);

  // Debounce the name field
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(name);
    }, 600); // 600ms debounce delay

    return () => clearTimeout(timer);
  }, [name]);

  const hasExerciseWithNameQuery =
    trpc.exerciseLibrary.hasExerciseWithName.useQuery(
      {
        name: debouncedName,
        excludeId: isEdit ? exercise?.id : undefined,
      },
      {
        enabled: !!open,
      },
    );
  const hasExerciseWithName = hasExerciseWithNameQuery.data ?? false;

  const templateCountQuery =
    trpc.exerciseLibrary.getTemplateCountForExercise.useQuery(
      { exerciseId: exercise?.id ?? '' },
      { enabled: isEdit && !isDefault },
    );
  const templateCount = templateCountQuery.data ?? 0;

  const getDeleteTooltip = () => {
    if (disableDelete) {
      return {
        title: t('permissionDisabled.delete', { item: t('exercises') }),
        description: '',
      };
    }
    if (isDefault) {
      return {
        title: t('thisExerciseCannotBeDeleted'),
        description: t('thisIsADefaultExercise'),
      };
    }
    if (templateCount > 0) {
      return {
        title: t('thisExerciseCannotBeDeleted'),
        description: t('xWorkoutTemplatesAreUsingThisExercise', {
          count: templateCount,
        }),
      };
    }
    return null;
  };

  const deleteTooltip = getDeleteTooltip();
  const isDeleteDisabled =
    isDefault ||
    templateCountQuery.isLoading ||
    templateCount > 0 ||
    disableDelete;

  const onHandleDelete = () => {
    onDelete?.(exercise);
    onOpenChange();
  };

  // The permission reason is also associated programmatically (not just via
  // the hover tooltip) so it is announced for the disabled control.
  const deleteReasonId = useId();

  const validateIntegrationProperties = (
    properties: ExerciseMetricSelection['integrationProviderProperties'],
  ) => {
    const filteredProperties = properties.filter(
      ({ provider, property, value }) =>
        !(provider === '' && property === '' && value === ''),
    );

    try {
      return zIntegrationPropertiesFormSchema.parse(filteredProperties);
    } catch (error) {
      form.setError('integrationProviderProperties', {
        type: 'manual',
        message: t('invalidIntegrationSettings'),
      });
      return false;
    }
  };

  const onFormSubmit = (scheme: ExerciseDetails | ExerciseMetricSelection) => {
    if (step === 'details') {
      setDetailsData(scheme as ExerciseDetails);
      setStep('metrics');
    } else {
      const metricsData = scheme as ExerciseMetricSelection;
      const validatedProperties = validateIntegrationProperties(
        metricsData.integrationProviderProperties,
      );

      if (!validatedProperties) return;

      const exerciseFormValues = ExerciseFormSchema.parse({
        ...detailsData,
        ...metricsData,
        integrationProviderProperties: validatedProperties,
      });
      onSubmit({ id: exercise?.id ?? '', exercise: exerciseFormValues });
      onClose();
    }
  };

  const onClose = () => {
    onOpenChange();
    setStep('details');
    reset();
  };

  const getTeamName = () => {
    const id = isEdit ? exercise?.teamId : teamId;
    if (!id) return t('baseLibrary');
    return teams.find((team) => team.id === id)?.name ?? '';
  };

  const isTeamScoped = showTeamName || isEdit;
  const getSubtitle = () => {
    if (enableTeamLibrary && isTeamScoped) {
      return (
        <Typography variant="body2" color={mediumEmphasisTextLight}>
          {getTeamName()}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color={mediumEmphasisTextLight}>
        {step === 'details'
          ? t('stepOneOfTwoDetails')
          : t('stepTwoOfTwoMetricSelection')}
      </Typography>
    );
  };

  return (
    <Drawer open={open} onClose={onClose} anchor="right">
      <DrawerHeader onClose={onClose}>
        <div className="flex items-center gap-2">
          <Typography variant="h3">
            {isEdit ? exercise?.name : t('createExercise')}
          </Typography>
          {isEdit && exercise?.isDefault && (
            <Badge {...defaultExerciseBadge(t)} />
          )}
        </div>

        {getSubtitle()}
      </DrawerHeader>

      <Form {...form}>
        <form
          id="library-exercise-form"
          onSubmit={handleSubmit(onFormSubmit)}
          className="flex flex-col grow h-full overflow-hidden"
        >
          <div className="px-8 py-4 grow overflow-y-scroll overflow-x-hidden">
            {hasExerciseWithName && (
              <Alert severity="error">
                <AlertTitle>
                  {t('exerciseNameMustBeUnique', { name })}
                </AlertTitle>
              </Alert>
            )}
            <ExerciseFormContent
              form={form}
              step={step}
              bodyPartOptions={bodyParts}
              equipmentOptions={equipment}
              tagOptions={tags}
              trainingTypeOptions={trainingTypes}
              hasInitialDefaultValue={!!defaultValues?.name}
              showLibraryField={enableTeamLibrary && !isTeamScoped}
              isReadOnly={disableWrite}
              requireMetadataFields={requireExerciseMetadataFields}
            />
          </div>
        </form>
      </Form>
      <DrawerFooter className="flex flex-row w-full p-8 justify-between">
        <div className="flex flex-row gap-2">
          {isEdit && (
            <Tooltip
              title={
                deleteTooltip && (
                  <div>
                    <Typography variant="subtitle1Semibold" color={white}>
                      {deleteTooltip.title}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color={mediumEmphasisTextDark}
                    >
                      {deleteTooltip.description}
                    </Typography>
                  </div>
                )
              }
              arrow
            >
              <span>
                <Button
                  variant="containedDestructive"
                  onClick={() => setDeleteModalOpen(true)}
                  aria-describedby={disableDelete ? deleteReasonId : undefined}
                  disabled={isDeleteDisabled}
                >
                  {t('delete')}
                </Button>
                {disableDelete && (
                  <span id={deleteReasonId} className="sr-only">
                    {deleteTooltip?.title}
                  </span>
                )}
              </span>
            </Tooltip>
          )}
          <Button variant="outlined" onClick={onOpenChange}>
            {t('cancel')}
          </Button>
        </div>
        <div className="flex flex-row gap-2">
          {step === 'metrics' && (
            <Button variant="outlined" onClick={() => setStep('details')}>
              {t('previous')}
            </Button>
          )}

          <Button
            type="submit"
            form="library-exercise-form"
            disabled={
              hasExerciseWithName || (disableWrite && step === 'metrics')
            }
          >
            {step === 'details' ? t('next') : t('save')}
          </Button>
        </div>
      </DrawerFooter>
      {isEdit && !isDeleteDisabled && (
        <DeleteConfirmationModal
          id={exercise?.id ?? ''}
          open={deleteModalOpen}
          {...deleteExerciseText(t)}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={() => onHandleDelete()}
        />
      )}
    </Drawer>
  );
};

const getValuesForExerciseForm = (exercise: LibraryExercise): ExerciseForm => {
  const { bodyParts, equipment, defaultMetrics, tags, ...rest } = exercise;
  return {
    ...rest,
    bodyParts: bodyParts.map(({ bodyPart: { id, name } }) => {
      return { id, name };
    }),
    equipment: equipment.map(({ equipment: { id, name } }) => {
      return { id, name };
    }),
    tags: tags.map(({ tag: { id, name, type } }) => {
      return { id, name, type };
    }),
    defaultMetrics: defaultMetrics.map(
      ({ metric: { id, name, type, config } }) => {
        return { id, name, type, config };
      },
    ),
    integrationProviderProperties: Object.entries(
      exercise.integrationProperties ?? {},
    ).flatMap(([provider, properties]) =>
      Object.entries(properties).map(([property, value]) => ({
        provider,
        property,
        value,
      })),
    ),
  };
};

const createDefaultExerciseForm = (
  initialValues?: Partial<ExerciseForm>,
): ExerciseForm => ({
  name: initialValues?.name || '',
  teamId: initialValues?.teamId || undefined,
  trainingType: initialValues?.trainingType ?? null,
  imageUrl: initialValues?.imageUrl || null,
  videoUrl: initialValues?.videoUrl || null,
  bodyParts: initialValues?.bodyParts || [],
  equipment: initialValues?.equipment || [],
  tags: initialValues?.tags || [],
  defaultMetrics: initialValues?.defaultMetrics || [],
  integrationProviderProperties:
    initialValues?.integrationProviderProperties || [],
});
